/**
 * HumanRelationship — links a Human to an objective or result with a typed relationship.
 *
 * Uses .version field convention (no version suffix in type name), per Collaborator.ts pattern.
 * Uses TypeScript `type` (not interface) per project convention for Firestore data.
 */

export type HumanRelationship = {
  /** Primary identifier */
  uuid: string;
  /** First 10 chars of uuid, used in URLs */
  shortId: string;
  /** UUID of the Human this relationship belongs to */
  humanUuid: string;
  /** Type of the target entity */
  targetType: 'objective' | 'result';
  /** UUID of the target objective or result */
  targetUuid: string;
  /** Freeform kebab-case relationship type (e.g., "project-lead") */
  relationshipType: string;
  /** Human-readable label that generates relationshipType (e.g., "Project Lead") */
  relationshipName?: string;
  /** ISO 8601 start date for this relationship */
  startDate?: string;
  /** ISO 8601 end date for this relationship (undefined = open-ended) */
  endDate?: string;
  /** Schema version for migration support */
  version: number;
  /** ISO 8601 creation timestamp */
  createdDate: string;
  /** UUID of the user who created this record */
  createdBy?: string;
};

/**
 * Generates a deterministic Firestore document ID for a HumanRelationship.
 *
 * Format: `${relationshipType}__${humanUuid}__${targetUuid}`
 *
 * This ensures that a given human can only hold one relationship of a specific type
 * to a specific target (unless date ranges are used to create versioned assignments).
 */
export function generateRelationshipDocId(
  relationshipType: string,
  humanUuid: string,
  targetUuid: string
): string {
  return `${relationshipType}__${humanUuid}__${targetUuid}`;
}

/**
 * Converts a human-readable relationship name to a kebab-case relationshipType.
 * Strips all special characters, keeping only a-z, 0-9, and hyphens.
 *
 * Examples:
 *   "Project Lead" → "project-lead"
 *   "  Technical  Reviewer " → "technical-reviewer"
 *   "SCRUM Master" → "scrum-master"
 *   "Sr. Engineer (Contract)" → "sr-engineer-contract"
 *   "Team-Member #1" → "team-member-1"
 */
export function toRelationshipType(relationshipName: string): string {
  return relationshipName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Checks whether a new HumanRelationship's date range overlaps with any existing
 * relationships for the same humanUuid + targetUuid pair.
 *
 * Rules:
 * - Only relationships with the same humanUuid AND targetUuid are compared.
 *   Different targets (objectives/results) are always allowed.
 * - An open-ended range (no endDate) extends to infinity.
 * - A relationship with no startDate is treated as starting at the beginning of time.
 * - Two ranges overlap if one starts before the other ends AND vice versa.
 *
 * @returns `true` if the new relationship overlaps with an existing one (i.e., should be rejected).
 */
export function hasOverlappingDateRange(
  existingRelationships: HumanRelationship[],
  newRelationship: Pick<HumanRelationship, 'humanUuid' | 'targetUuid' | 'startDate' | 'endDate'>
): boolean {
  const relevant = existingRelationships.filter(
    (r) => r.humanUuid === newRelationship.humanUuid && r.targetUuid === newRelationship.targetUuid
  );

  for (const existing of relevant) {
    // Treat missing startDate as beginning of time, missing endDate as end of time
    const existingStart = existing.startDate ?? '';
    const existingEnd = existing.endDate ?? '\uffff'; // Max string sorts after any ISO date
    const newStart = newRelationship.startDate ?? '';
    const newEnd = newRelationship.endDate ?? '\uffff';

    // Two ranges [A_start, A_end] and [B_start, B_end] overlap when:
    // A_start < B_end AND B_start < A_end
    if (existingStart < newEnd && newStart < existingEnd) {
      return true;
    }
  }

  return false;
}
